/**
 * API Client utilities for DFF Flow Frontend
 * Based on cptbl patterns
 */

import { API_URL } from '@/config/api';
import { errorMessage as toErrorMessage, log } from '@/utils/logger';

// API configuration available for debugging via window._apiConfig in development
if (import.meta.env.DEV) {
  (window as typeof window & { _apiConfig?: unknown })._apiConfig = {
    API_URL,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    DEV: import.meta.env.DEV,
  };
}

const FILE_TOO_LARGE_MESSAGE = 'File too large. Maximum upload size is 10MB.';

// Without a deadline a stalled connection leaves the caller pending forever.
// Uploads push large bodies over slow links, so they get a longer one.
const REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 120_000;

interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

export function extractDetailMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: ValidationError) => e.msg).join('; ');
  }
  return undefined;
}

/** Pull the human-readable message out of a parsed FastAPI error body. */
function extractErrorMessage(data: unknown, fallback: string): string {
  const body = data as
    | { detail?: unknown; error?: { message?: string }; message?: string }
    | null
    | undefined;

  return (
    extractDetailMessage(body?.detail) ||
    body?.error?.message ||
    body?.message ||
    fallback
  );
}

export class APIError extends Error {
  public validationErrors?: Record<string, string>;

  constructor(
    message: string,
    public status: number,
    public data?: unknown,
    // Correlation id sent as X-Request-ID; lets the frontend error log join to
    // the backend request logs in BetterStack.
    public requestId?: string
  ) {
    super(message);
    this.name = 'APIError';

    // Extract field-level validation errors from response
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = data.detail;
      if (Array.isArray(detail)) {
        this.validationErrors = this.parseValidationErrors(detail);
      }
    }
  }

  /**
   * Parse FastAPI validation errors into a field-error map
   * FastAPI returns errors like: { loc: ["body", "field_name"], msg: "error message" }
   */
  private parseValidationErrors(
    errors: ValidationError[]
  ): Record<string, string> {
    const fieldErrors: Record<string, string> = {};

    for (const error of errors) {
      // loc is an array like ["body", "field_name"] or ["body", "nested", "field"]
      // We want to extract the field path after "body"
      const fieldPath = error.loc.slice(1).join('.');
      if (fieldPath) {
        fieldErrors[fieldPath] = error.msg;
      }
    }

    return fieldErrors;
  }
}

// Helper function to remove undefined values from objects
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(cleanUndefinedValues);
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, cleanUndefinedValues(value)])
    );
  }

  return obj;
}

function toRequestBody(data: unknown): BodyInit | undefined {
  if (data instanceof FormData) return data;
  if (data === undefined || data === null) return undefined;
  return JSON.stringify(cleanUndefinedValues(data));
}

/**
 * Serialize query params. An array repeats its key (`?id=1&id=2`) because that
 * is the shape FastAPI parses back into a list. Empty strings are dropped so a
 * cleared filter input means "no filter", not a `?search=` the API must parse.
 */
function buildQueryString(params?: object): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== undefined && item !== null && item !== '') {
        searchParams.append(key, String(item));
      }
    }
  }

  return searchParams.toString();
}

/**
 * Merge caller headers over ours, matching case-insensitively so an override
 * replaces the base value instead of fetch sending both comma-joined.
 */
function mergeHeaders(
  base: Record<string, string>,
  extra?: HeadersInit
): Record<string, string> {
  if (!extra) return base;

  const merged = { ...base };
  const canonical = new Map(
    Object.keys(base).map((key) => [key.toLowerCase(), key])
  );
  new Headers(extra).forEach((value, key) => {
    merged[canonical.get(key) ?? key] = value;
  });

  return merged;
}

/**
 * Signal that aborts on timeout or when the caller aborts. Built from a plain
 * AbortController because AbortSignal.any/timeout are missing in jsdom and in
 * older Safari. Call release() to drop the timer and listener.
 */
function timeoutSignal(
  callerSignal: AbortSignal | null | undefined,
  timeoutMs: number
): { signal: AbortSignal; release: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(
    () =>
      controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    timeoutMs
  );
  const onCallerAbort = () => controller.abort(callerSignal?.reason);

  if (callerSignal?.aborted) {
    onCallerAbort();
  } else {
    callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  }

  return {
    signal: controller.signal,
    release: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

/**
 * True for our own timeout and for a plain fetch abort. Duck-typed on name:
 * a DOMException raised in another realm (jsdom, an iframe) fails instanceof.
 */
function isAbortError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/**
 * Correlation id for cross-app tracing. Reuse a caller-supplied id
 * (X-Client-Trace-Id / X-Request-ID) so a browser log event and the backend
 * request logs share one value; otherwise generate one. The backend echoes it
 * back as X-Request-ID and stamps it onto every log line for that request.
 */
export function resolveRequestId(headers?: HeadersInit): string {
  if (headers) {
    // Headers normalizes all three HeadersInit shapes and matches case-insensitively.
    const lookup = new Headers(headers);
    const existing =
      lookup.get('X-Client-Trace-Id') ?? lookup.get('X-Request-ID');
    if (existing) return existing;
  }
  return crypto.randomUUID();
}

class APIClient {
  private baseURL: string;
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(baseURL: string = API_URL) {
    this.baseURL = baseURL;
  }

  private buildUrl(endpoint: string): string {
    return endpoint.startsWith('http')
      ? endpoint
      : `${this.baseURL}${endpoint}`;
  }

  /**
   * Get authentication headers if token exists
   */
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Exchange the httponly refresh cookie for a new access token.
   * Raw fetch, not this.request(), so a 401 here cannot recurse.
   */
  private async fetchNewAccessToken(): Promise<boolean> {
    const { signal, release } = timeoutSignal(null, REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': crypto.randomUUID(),
        },
        credentials: 'include',
        signal,
      });

      if (!response.ok) return false;

      const data = (await response.json()) as { access_token?: unknown };
      if (typeof data?.access_token !== 'string' || !data.access_token) {
        return false;
      }

      localStorage.setItem('access_token', data.access_token);
      return true;
    } catch (error) {
      log.warn('Token refresh failed', {
        error_message: toErrorMessage(error),
      });
      return false;
    } finally {
      release();
    }
  }

  /**
   * Refresh the access token, reusing an in-flight refresh so N concurrent
   * 401s (e.g. a whole page refetching on window focus) trigger one call.
   */
  private refreshAccessToken(): Promise<boolean> {
    this.refreshInFlight ??= this.fetchNewAccessToken().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /**
   * Fetch, retrying once on 401 with a refreshed access token, so a token that
   * expired while the tab was idle never surfaces to the caller. The config is
   * rebuilt per attempt to pick up the new Authorization header.
   */
  private async fetchWithAuth(
    url: string,
    buildConfig: () => RequestInit
  ): Promise<Response> {
    const attempt = async (): Promise<Response> => {
      const response = await fetch(url, buildConfig());
      if (response.status === 413) {
        throw new APIError(FILE_TOO_LARGE_MESSAGE, 413);
      }
      return response;
    };

    const response = await attempt();
    if (response.status !== 401) return response;

    // These endpoints must not trigger a refresh:
    if (url.includes('/auth/refresh') || url.includes('/auth/logout')) {
      throw new APIError('Unauthorized', 401);
    }
    // Without a token there is no session to renew (e.g. logged out elsewhere).
    if (!localStorage.getItem('access_token')) {
      this.failUnauthorized('Unauthorized');
    }
    if (!(await this.refreshAccessToken())) {
      this.failUnauthorized('Session expired');
    }

    const retried = await attempt();

    if (retried.status === 401) this.failUnauthorized('Unauthorized');
    return retried;
  }

  private failUnauthorized(message: string): never {
    window.dispatchEvent(
      new CustomEvent('auth:unauthorized', { detail: { skipRefresh: true } })
    );
    throw new APIError(message, 401);
  }

  /**
   * Parse error message from failed response
   * Attempts to extract error details from JSON response body
   */
  private async parseErrorMessage(response: Response): Promise<string> {
    const fallback = `Request failed: ${response.statusText}`;
    try {
      return extractErrorMessage(await response.json(), fallback);
    } catch {
      // If JSON parsing fails, use the default error message
      return fallback;
    }
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = this.buildUrl(endpoint);

    const isFormData = options.body instanceof FormData;
    const requestId = resolveRequestId(options.headers);
    const { headers: callerHeaders, ...restOptions } = options;

    // Each attempt gets its own timeout, so the 401 replay starts a fresh clock.
    const releases: Array<() => void> = [];
    const buildConfig = (): RequestInit => {
      const { signal, release } = timeoutSignal(
        options.signal,
        isFormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS
      );
      releases.push(release);

      return {
        ...restOptions,
        // Headers last: spreading the caller's options over them dropped
        // Content-Type, auth and the correlation id.
        headers: mergeHeaders(
          {
            ...(!isFormData && { 'Content-Type': 'application/json' }),
            ...this.getAuthHeaders(),
            'X-Request-ID': requestId,
          },
          callerHeaders
        ),
        signal,
      };
    };

    try {
      const response = await this.fetchWithAuth(url, buildConfig);

      if (response.status === 204) {
        return {} as T;
      }

      // Handle JSON responses
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        log.error('Failed to parse JSON response', {
          error_message: toErrorMessage(parseError),
          status: response.status,
          request_id: requestId,
        });
        // Provide better error messages for common status codes
        if (response.status === 404) {
          throw new APIError('Resource not found', 404);
        } else if (response.status === 500) {
          throw new APIError('Server error. Please try again later.', 500);
        }
        throw new APIError(
          'Invalid JSON response from server',
          response.status
        );
      }

      if (!response.ok) {
        const errorMessage = extractErrorMessage(data, 'API request failed');

        const level = response.status === 404 ? 'warn' : 'error';
        log[level]('API error', {
          status: response.status,
          method: (options.method ?? 'GET').toUpperCase(),
          error_message: errorMessage,
          endpoint,
          request_id: requestId,
        });
        throw new APIError(errorMessage, response.status, data);
      }

      // For scheduler endpoints and FastAPI, return data directly
      // Ensure we never return undefined
      if (data === undefined || data === null) {
        log.warn('API returned null/undefined, returning empty object', {
          endpoint,
        });
        return {} as T;
      }

      return data as T;
    } catch (error) {
      this.rethrow(error, requestId, options.signal);
    } finally {
      releases.forEach((release) => release());
    }
  }

  /**
   * Normalize a thrown value into an APIError carrying this request's id.
   * A caller-driven abort (React Query unmount, refetch) is rethrown as-is so
   * it reads as a cancellation rather than a network failure.
   */
  private rethrow(
    error: unknown,
    requestId: string,
    callerSignal?: AbortSignal | null
  ): never {
    if (error instanceof APIError) {
      // Tag with this request's correlation id (covers throws from the
      // helpers, e.g. 401/413) so it reaches the error log downstream.
      error.requestId ??= requestId;
      throw error;
    }
    if (callerSignal?.aborted) throw error;
    if (isAbortError(error)) {
      throw new APIError(
        'Request timed out. Please try again.',
        0,
        error,
        requestId
      );
    }
    throw new APIError('Network error', 0, error, requestId);
  }

  async get<T = unknown>(
    endpoint: string,
    options?: { params?: object; signal?: AbortSignal }
  ): Promise<T> {
    const query = buildQueryString(options?.params);
    const separator = endpoint.includes('?') ? '&' : '?';
    const url = query ? `${endpoint}${separator}${query}` : endpoint;

    return this.request<T>(url, { method: 'GET', signal: options?.signal });
  }

  async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: toRequestBody(data),
      ...options,
    });
  }

  async put<T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: toRequestBody(data),
      ...options,
    });
  }

  async patch<T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: toRequestBody(data),
      ...options,
    });
  }

  async delete<T = unknown>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  /**
   * Fetch a file as a Blob (for viewing/processing in memory)
   * Does not trigger a browser download
   */
  async getBlob(
    endpoint: string,
    options?: { signal?: AbortSignal }
  ): Promise<Blob> {
    const url = this.buildUrl(endpoint);
    const requestId = resolveRequestId();

    const releases: Array<() => void> = [];
    const buildConfig = (): RequestInit => {
      const { signal, release } = timeoutSignal(
        options?.signal,
        REQUEST_TIMEOUT_MS
      );
      releases.push(release);

      return {
        headers: {
          ...this.getAuthHeaders(),
          'X-Request-ID': requestId,
        },
        signal,
      };
    };

    try {
      const response = await this.fetchWithAuth(url, buildConfig);

      if (!response.ok) {
        const errorMessage = await this.parseErrorMessage(response);
        throw new APIError(errorMessage, response.status, undefined, requestId);
      }

      return await response.blob();
    } catch (error) {
      this.rethrow(error, requestId, options?.signal);
    } finally {
      releases.forEach((release) => release());
    }
  }
}

export const apiClient = new APIClient();
